// src/sign_in_files/VerifyMobileNumber.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LoadingOverlay,
  SuccessModal,
  AutoSaveIndicator,
  OTPInput,
  useAutoSave,
  ErrorMessage,
} from './DesignSystem';

const VerifyMobileNumber = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Clear any old fundraiser data when starting fresh verification
  useEffect(() => {
    localStorage.removeItem('fundraiserId');
    localStorage.removeItem('submittedFundraiserId');
    localStorage.removeItem('submissionTime');
    localStorage.removeItem('fundraiser_details');
  }, []);

  // Get initial mobile - can be edited
  const getInitialMobile = () => {
    if (location.state?.mobile) return location.state.mobile;
    
    const savedData = localStorage.getItem('tempUserData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        return parsed.mobile || '';
      } catch (err) {
        return '';
      }
    }
    return '';
  };

  // ✅ FIXED: Get userId - recomputes when location.state changes
  const userId = React.useMemo(() => {
    // Check navigation state first (always latest)
    if (location.state?.userId) {
      console.log('✅ VerifyMobile: Got userId from navigation state:', location.state.userId);
      return location.state.userId;
    }
    
    // Fallback to localStorage
    const savedData = localStorage.getItem('tempUserData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.userId) {
          console.log('✅ VerifyMobile: Got userId from localStorage:', parsed.userId);
          return parsed.userId;
        }
      } catch (err) {}
    }
    
    console.warn('⚠️ VerifyMobile: No userId found');
    return null;
  }, [location.state?.userId]);

  // ✅ FIXED: Get otpTxnId - recomputes when location.state changes
  const otpTxnId = React.useMemo(() => {
    if (location.state?.otpTxnId) return location.state.otpTxnId;
    
    const savedData = localStorage.getItem('tempUserData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        return parsed.otpTxnId || null;
      } catch (err) {
        return null;
      }
    }
    return null;
  }, [location.state?.otpTxnId]);

  // State
  const [mobile, setMobile] = useState(getInitialMobile());
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [countdown, setCountdown] = useState(30);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(0);

  // Auto-save setup
  const { lastSaved, isSaving, saveError, loadDraft, clearDraft } = useAutoSave(
    { otp: otp.join('') },
    'mobile_verification'
  );

  const features = [
    {
      title: "Secure Verification",
      description: "Bank-level security for your information",
      gradient: "from-blue-500 to-cyan-600",
      icon: "🔒"
    },
    {
      title: "Quick Process",
      description: "Verify in seconds and start fundraising",
      gradient: "from-green-500 to-emerald-600",
      icon: "⚡"
    },
    {
      title: "24/7 Support",
      description: "We're here to help you succeed",
      gradient: "from-purple-500 to-indigo-600",
      icon: "🛡️"
    }
  ];

  // Component Init
  useEffect(() => {
    const draft = loadDraft();
    if (draft) {
      if (draft.otp && draft.otp.length === 6) {
        setOtp(draft.otp.split(''));
      }
    }

    const interval = setInterval(() => {
      setCurrentFeature(prev => (prev + 1) % features.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Format mobile
  const formatMobileNumber = (number) => {
    if (!number || number.length !== 10) return number;
    return `${number.substring(0, 5)} ${number.substring(5)}`;
  };

  // OTP Complete handler
  const handleOTPComplete = (otpString) => {
    // Optional: Auto-submit when OTP is complete
  };

  // ✅ FIXED: Submit OTP Verification
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      setApiError("User ID not found. Please go back to registration.");
      return;
    }

    if (!otpTxnId) {
      setApiError("OTP session expired. Please go back to registration.");
      return;
    }

    if (!mobile) {
      setApiError("Mobile number missing. Please go back to registration.");
      return;
    }

    const otpString = otp.join('');
    if (!otpString || otpString.length !== 6) {
      setApiError("Please enter the complete 6-digit OTP.");
      return;
    }

    try {
      setIsLoading(true);
      setApiError("");

      localStorage.removeItem('submittedFundraiserId');
      localStorage.removeItem('submissionTime');
      localStorage.removeItem('fundraiserId');
      localStorage.removeItem('fundraiser_details');

      console.log('📤 VerifyMobile: Sending OTP verification with userId:', userId);

      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          userId: userId,
          otpTxnId: otpTxnId,
          otp: otpString
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData?.message || "OTP verification failed");
      }

      const verificationResponse = await response.json();
      
      clearDraft();

      // Save verification data with userId
      const verificationData = {
        mobile,
        userId,
        otpTxnId,
        otpVerified: true,
        verifiedAt: new Date().toISOString(),
        ...verificationResponse
      };

      localStorage.setItem("completeUserData", JSON.stringify(verificationData));
      localStorage.removeItem("tempUserData");

      setVerificationSuccess(true);
      setShowSuccessModal(true);

      // ✅ FIXED: Navigate to DetailsScreen with userId
      console.log('✅ VerifyMobile: Verification successful, navigating with userId:', userId);
      
      setTimeout(() => {
        navigate("/detailsScreen", {
          state: { 
            userId: userId,
            isNewVerification: true
          }
        });
      }, 2000);

    } catch (err) {
      setApiError(err.message || "OTP verification failed");

      if (
        err.message.toLowerCase().includes("invalid") ||
        err.message.toLowerCase().includes("wrong") ||
        err.message.toLowerCase().includes("incorrect")
      ) {
        setOtp(['', '', '', '', '', '']);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (countdown > 0) return;

    try {
      setIsLoading(true);
      
      console.log('📤 VerifyMobile: Resending OTP for userId:', userId);
      
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/resend-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          userId: userId,
          otpTxnId: otpTxnId
        })
      });

      if (!response.ok) {
        throw new Error("Failed to resend OTP");
      }

      setCountdown(30);
      setApiError({ type: 'info', message: 'OTP resent successfully' });
    } catch (error) {
      setApiError(error.message || 'Failed to resend OTP');
    } finally {
      setIsLoading(false);
    }
  };

  // API Error renderer
  const renderApiError = () => {
    if (!apiError) return null;
    if (typeof apiError === "object") {
      return <ErrorMessage message={apiError.message} type={apiError.type} />;
    }
    return <ErrorMessage message={apiError} type="error" />;
  };

  // Debug info
  const renderDebugInfo = () => {
    if (process.env.NODE_ENV === 'production') return null;
    
    return (
      <div className="mb-4 p-2 bg-gray-100 border border-gray-200 rounded text-xs">
        <details>
          <summary className="cursor-pointer font-medium">Debug Info</summary>
          <div className="mt-2 space-y-1">
            <p><span className="font-medium">User ID:</span> {userId || '❌ Not found'}</p>
            <p><span className="font-medium">OTP Txn ID:</span> {otpTxnId || '❌ Not found'}</p>
            <p><span className="font-medium">Mobile:</span> {mobile || '❌ Not found'}</p>
            <p><span className="font-medium">State:</span> {JSON.stringify(location.state, null, 2)}</p>
          </div>
        </details>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gsg-teal/5">
      {isLoading && <LoadingOverlay message="Verifying OTP..." />}

      <SuccessModal
        isOpen={showSuccessModal}
        title="Mobile Verified Successfully!"
        message="Your mobile number has been verified. You'll be redirected to the next step in a moment."
        showCountdown={true}
        countdownDuration={2}
      />

      <AutoSaveIndicator
        lastSaved={lastSaved}
        isSaving={isSaving}
        saveError={saveError}
      />

      <div className="relative min-h-screen flex flex-col lg:flex-row">
        {/* Left Section */}
        <div className="hidden lg:flex lg:w-1/3 relative bg-gradient-to-br from-gsg-teal/5 via-white to-white">
          <div className="relative z-10 flex flex-col justify-center items-start w-full h-full p-6 lg:p-12">
            <div className="max-w-lg w-full">
              <div className="mb-8 lg:mb-12 transition-all duration-1000">
                <div className="inline-flex items-center justify-center w-16 h-16 lg:w-24 lg:h-24 bg-gradient-to-br from-gsg-teal to-gsg-teal-dark rounded-xl lg:rounded-2xl mb-6 lg:mb-8 shadow-xl lg:shadow-2xl shadow-gsg-teal/20">
                  <svg className="w-8 h-8 lg:w-12 lg:h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>

                <h1 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6 leading-tight">
                  Verify Your <span className="text-gsg-teal">Mobile</span>
                </h1>

                <p className="text-base lg:text-xl text-gray-600 leading-relaxed">
                  Secure your account with mobile verification.
                </p>
              </div>

              <div className="transition-all duration-1000 delay-300">
                <div className="space-y-4 lg:space-y-5">
                  {features.map((feature, index) => (
                    <div
                      key={index}
                      className={`p-4 lg:p-5 bg-white/95 backdrop-blur-sm rounded-xl lg:rounded-2xl border-2 transition-all duration-500 
                        ${currentFeature === index
                          ? 'border-gsg-teal/30 shadow-lg lg:shadow-xl shadow-gsg-teal/10 scale-[1.02]'
                          : 'border-gray-100 shadow-sm hover:border-gray-200'
                        }`}
                    >
                      <div className="flex items-start space-x-3 lg:space-x-4">
                        <div className={`w-10 h-10 lg:w-14 lg:h-14 rounded-lg lg:rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
                          <span className="text-xl lg:text-2xl">{feature.icon}</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 text-base lg:text-lg mb-1 lg:mb-1.5">{feature.title}</h4>
                          <p className="text-gray-600 text-xs lg:text-sm leading-relaxed">{feature.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="w-full lg:w-2/3 flex flex-col justify-center items-center py-4 px-4 lg:px-8">
          <div className="w-full max-w-3xl mx-auto">
            <div className="text-center mb-6 lg:mb-10 w-full">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight">Verify Your Mobile Number</h2>
              <p className="text-gray-600 mt-2 lg:mt-3 text-base lg:text-lg">Enter the OTP sent to your registered mobile number</p>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-center space-x-6 lg:space-x-12 mb-6 lg:mb-10 w-full px-4">
              {[1, 2].map((item, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className={`
                    w-10 h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center text-sm lg:text-base font-bold
                    ${index === 1 ? 'bg-gsg-teal text-white shadow-md lg:shadow-lg' : 'bg-gray-100 text-gray-500'}
                  `}>
                    {index + 1}
                  </div>
                  <div className="text-xs lg:text-sm font-semibold text-gray-700 mt-2 lg:mt-3 text-center">
                    {index === 0 ? 'Registration' : 'Verify OTP'}
                  </div>
                  <div className="h-1 w-full bg-gray-200 mt-2 lg:mt-3 rounded-full">
                    <div className={`h-1 w-full ${index === 0 ? 'bg-gsg-teal' : 'bg-gray-200'} rounded-full`}></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Debug Info */}
            {renderDebugInfo()}

            <div className="w-full flex justify-center px-4 lg:px-0">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 w-full max-w-2xl">
                <div className="px-4 lg:px-6 py-4 lg:py-5 border-b border-gray-100 bg-gradient-to-r from-gsg-teal/5 to-white">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-lg bg-gradient-to-br from-gsg-teal to-gsg-teal-dark flex items-center justify-center shadow-sm">
                      <span className="text-base lg:text-lg text-white">📱</span>
                    </div>
                    <div>
                      <h3 className="text-lg lg:text-xl font-bold text-gray-900">
                        Mobile Verification
                      </h3>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Step 2 of 2: Enter OTP to verify your mobile
                      </p>
                    </div>
                  </div>
                </div>

                <div className="px-4 lg:px-6 py-4 lg:py-5">
                  {renderApiError()}

                  {verificationSuccess && !showSuccessModal && (
                    <div className="mb-4 lg:mb-6 p-3 lg:p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-2 lg:space-x-3">
                        <svg className="w-4 h-4 lg:w-5 lg:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <p className="text-xs lg:text-sm text-green-700 font-medium">
                          Verification successful! Preparing your account...
                        </p>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
                    {/* Mobile */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                        Mobile Number <span className="text-red-500">*</span>
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          (This field cannot be changed)
                        </span>
                      </label>

                      <div className="relative">
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                          <span className="text-gray-700 font-medium text-xs lg:text-sm">🇮🇳 +91</span>
                        </div>

                        <input
                          type="tel"
                          value={formatMobileNumber(mobile)}
                          readOnly
                          className="w-full pl-16 lg:pl-20 px-3 lg:px-3.5 py-2.5 lg:py-3 rounded-lg border-2 bg-gray-50 border-gray-200 text-gray-700 text-sm cursor-not-allowed"
                          placeholder="10-digit mobile number"
                          maxLength="15"
                        />

                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <svg className="w-4 h-4 lg:w-5 lg:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                              d="M12 15v2m-6 4h12a2 2 0 002-2V5a2 2 0 00-2-2H6a2 2 0 00-2 2v14a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                      </div>

                      <div className="mt-2 flex items-start lg:items-center text-xs">
                        <svg className="w-3 h-3 lg:w-4 lg:h-4 text-gray-400 mr-1 mt-0.5 lg:mt-0 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-gray-500">
                          OTP has been sent to +91 {formatMobileNumber(mobile)}. If incorrect, please go back to registration.
                        </span>
                      </div>
                    </div>

                    {/* OTP Section */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                        6-digit OTP <span className="text-red-500">*</span>
                      </label>

                      <div className="flex justify-center w-full">
                        <div className="w-full max-w-xs sm:max-w-sm md:max-w-md">
                          <div className="flex justify-between space-x-1.5 sm:space-x-2 md:space-x-3 w-full">
                            {[0, 1, 2, 3, 4, 5].map((index) => (
                              <input
                                key={index}
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={1}
                                value={otp[index]}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^[0-9]?$/.test(value)) {
                                    const newOtp = [...otp];
                                    newOtp[index] = value;
                                    setOtp(newOtp);
                                    
                                    if (value && index < 5) {
                                      const nextInput = document.getElementById(`otp-input-${index + 1}`);
                                      if (nextInput) nextInput.focus();
                                    }
                                    
                                    const otpString = newOtp.join('');
                                    if (otpString.length === 6) {
                                      handleOTPComplete(otpString);
                                    }
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Backspace' && !otp[index] && index > 0) {
                                    const prevInput = document.getElementById(`otp-input-${index - 1}`);
                                    if (prevInput) prevInput.focus();
                                  }
                                }}
                                onFocus={(e) => e.target.select()}
                                id={`otp-input-${index}`}
                                className={`
                                  w-full h-10 sm:h-12 md:h-14
                                  text-center text-lg sm:text-xl md:text-2xl font-bold
                                  border-2 rounded-lg
                                  focus:outline-none focus:ring-2 focus:ring-gsg-teal/50 focus:border-gsg-teal
                                  transition-all duration-200
                                  ${apiError && apiError.includes('OTP') 
                                    ? 'border-red-300 bg-red-50' 
                                    : 'border-gray-300 hover:border-gray-400'
                                  }
                                  ${otp[index] ? 'border-gsg-teal bg-gsg-teal/5' : ''}
                                `}
                                autoComplete="one-time-code"
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Resend */}
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={countdown > 0 || isLoading || !mobile}
                        className={`
                          text-xs lg:text-sm font-medium transition-all
                          ${countdown > 0 || isLoading || !mobile
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gsg-teal hover:text-gsg-teal-dark hover:underline'
                          }
                        `}
                      >
                        Resend OTP
                      </button>

                      {countdown > 0 && (
                        <span className="text-xs lg:text-sm text-gray-500 font-medium">
                          Resend in {countdown}s
                        </span>
                      )}
                    </div>

                    {/* Submit */}
                    <div className="mt-4 lg:mt-6 pt-4 lg:pt-5 border-t border-gray-100">
                      <button
                        type="submit"
                        disabled={isLoading || !mobile || !userId || !otpTxnId}
                        className={`
                          w-full px-4 lg:px-6 py-2.5 lg:py-3 rounded-lg transition-all duration-300 font-semibold text-sm
                          ${isLoading || !mobile || !userId || !otpTxnId
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-gsg-teal to-gsg-teal-dark text-white hover:shadow-md hover:shadow-gsg-teal/25'
                          }
                          flex items-center justify-center
                        `}
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                              </path>
                            </svg>
                            Verifying...
                          </>
                        ) : 'Verify & Continue'}
                      </button>
                    </div>
                  </form>

                  {/* Help */}
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-xs text-blue-700 text-left">
                        Having trouble receiving the OTP? Check your mobile number and network connection.
                      </p>
                    </div>
                  </div>

                  {/* Back */}
                  <div className="mt-4 lg:mt-6 text-center">
                    <button
                      onClick={() => navigate(-1)}
                      className="text-gsg-teal hover:text-gsg-teal-dark font-medium hover:underline text-xs lg:text-sm transition-colors"
                    >
                      ← Go back to registration
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-6 lg:mt-8 w-full px-4 lg:px-0">
              <p className="text-xs text-gray-500">
                Your mobile number is only used for verification and important updates.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyMobileNumber;